import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import { Users, PowerOff, Pencil, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../Components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '../../Components/ui/sheet';

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
const EMPTY_FORM = { email: '', firstName: '', lastName: '', phone: '', notes: '', emergencyContactName: '', emergencyContactPhone: '' };

const Clients = () => {
  const { tenantDomain } = useParams();
  const { updateTenantDomain } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/client`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`${response.status}`);
      const data = await response.json();
      setClients(data);
    } catch (err) {
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantDomain]);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchClients();
  }, [tenantDomain, fetchClients, updateTenantDomain]);

  const openCreateSheet = () => {
    setEditingClient(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setSheetOpen(true);
  };

  const openEditSheet = (client) => {
    setEditingClient(client);
    setForm({
      phone: client.phone ?? '',
      notes: client.notes ?? '',
      emergencyContactName: client.emergencyContactName ?? '',
      emergencyContactPhone: client.emergencyContactPhone ?? '',
    });
    setFormError(null);
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!editingClient) {
      if (!form.email.trim() || !form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
        setFormError('Email, first name, last name, and phone are required.');
        return;
      }
    } else {
      if (!form.phone.trim()) {
        setFormError('Phone is required.');
        return;
      }
    }

    setSaving(true);
    setFormError(null);
    try {
      const isEdit = !!editingClient;
      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/${tenantDomain}/client/${editingClient.clientId}`
        : `${process.env.REACT_APP_API_URL}/${tenantDomain}/client`;
      const body = isEdit
        ? {
            phone: form.phone.trim(),
            notes: form.notes.trim() || null,
            emergencyContactName: form.emergencyContactName.trim() || null,
            emergencyContactPhone: form.emergencyContactPhone.trim() || null,
          }
        : {
            email: form.email.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            phone: form.phone.trim(),
            notes: form.notes.trim() || null,
            emergencyContactName: form.emergencyContactName.trim() || null,
            emergencyContactPhone: form.emergencyContactPhone.trim() || null,
          };

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.status === 409) {
        setFormError('A client with this email already exists.');
        return;
      }
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchClients();
      setSheetOpen(false);
    } catch (err) {
      setFormError('Failed to save client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (client) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/client/${client.clientId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ isActive: true }),
        }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchClients();
    } catch (err) {
      setError('Failed to activate client. Please try again.');
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/client/${deactivateTarget.clientId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchClients();
      setDeactivateTarget(null);
    } catch (err) {
      setError('Failed to deactivate client. Please try again.');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your client profiles</p>
        </div>
        <Button onClick={openCreateSheet}>Add Client</Button>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Users size={36} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium">No clients yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your first client to get started.</p>
          <Button className="mt-4" onClick={openCreateSheet}>Add Client</Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Phone</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Account</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr
                  key={client.clientId}
                  onClick={() => navigate(`/${tenantDomain}/clients/${client.clientId}`)}
                  className={`border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${!client.isActive ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-3 font-medium">{client.firstName} {client.lastName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{client.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{client.phone ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${client.userActive ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {client.userActive ? 'Active' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${client.isActive ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {client.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditSheet(client); }}
                        className="p-1.5 rounded hover:bg-accent transition-colors"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      {client.isActive ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeactivateTarget(client); }}
                          className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
                          title="Deactivate"
                        >
                          <PowerOff size={14} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleActivate(client); }}
                          className="p-1.5 rounded hover:bg-accent transition-colors text-muted-foreground"
                          title="Activate"
                        >
                          <PowerOff size={14} />
                        </button>
                      )}
                      <ChevronRight size={15} className="text-muted-foreground ml-1" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingClient ? `${editingClient.firstName} ${editingClient.lastName}` : 'Add Client'}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {formError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{formError}</div>
              )}

              {!editingClient && (
                <>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium">Email <span className="text-destructive">*</span></label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputClass}
                      placeholder="client@example.com"
                    />
                    <span className="text-xs text-muted-foreground">A set-password email will be sent to this address.</span>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex flex-col gap-1.5 flex-1">
                      <label className="text-sm font-medium">First Name <span className="text-destructive">*</span></label>
                      <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5 flex-1">
                      <label className="text-sm font-medium">Last Name <span className="text-destructive">*</span></label>
                      <input
                        type="text"
                        value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Phone <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputClass}
                  placeholder="416-555-0100"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-none"
                  rows={3}
                  placeholder="Any notes about this client..."
                />
              </div>

              <div className="border-t pt-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Emergency Contact</p>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium">Name</label>
                    <input
                      type="text"
                      value={form.emergencyContactName}
                      onChange={(e) => setForm({ ...form, emergencyContactName: e.target.value })}
                      className={inputClass}
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium">Phone</label>
                    <input
                      type="text"
                      value={form.emergencyContactPhone}
                      onChange={(e) => setForm({ ...form, emergencyContactPhone: e.target.value })}
                      className={inputClass}
                      placeholder="416-555-0101"
                    />
                  </div>
                </div>
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={!!deactivateTarget} onOpenChange={(open) => { if (!open) setDeactivateTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Client</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to deactivate <strong>{deactivateTarget?.firstName} {deactivateTarget?.lastName}</strong>? Their account will remain intact but they will no longer appear as active.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} disabled={deactivating}>Cancel</Button>
            <Button
              onClick={handleDeactivate}
              disabled={deactivating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deactivating ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Clients;
